/**
 * PostAndReconcile — write the coded transactions into BQE Core, in windows, tying out after each.
 *
 * THIS IS THE PIECE THAT WAS MISSING. The server has had `commitPlan`, `commitRows`,
 * `reconcileWindow` and `reconcilePeriod` since the first day, and no screen ever called them:
 * all 217 checks of the 2025 catch-up were posted by driving those endpoints from a browser
 * console. That is what made Bookkeeping a tool that only worked with Claude in the room.
 *
 * The shape follows the routine that actually worked over a year of real spend, and the routine
 * is the safety. Do not add a button that skips a step:
 *
 *   1. pick a window          two weeks at a time — Matt's own rule, so a mistake is small and
 *                             is found while it is still cheap to fix
 *   2. DRY RUN                every row, what it will become, and why. Writes nothing.
 *   3. post                   the only write. Capped server-side, and idempotent: a row that
 *                             already carries a Core id is refused, so pressing it twice is safe
 *   4. tie out                every Core check compared against the sum of the rows it covers.
 *                             Counting rows cannot find a double-booking — a duplicate and an
 *                             omission both leave the count looking reasonable
 *
 * Holds are not failures. A held row is the tool refusing to guess, and its reason says what a
 * human has to decide. See docs/BOOKKEEPING.md.
 */

import { useState, useEffect } from 'react'
import { planCommit, commitRows, reconcileWindow, reconcilePeriod } from '../../services/financeImport.js'
import { confirmDialog, alertDialog } from '../Dialog.jsx'

const money = (n) => (Number(n) < 0 ? '-' : '') + '$' +
  Math.abs(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** Two weeks from a start date — the window size the catch-up actually used. */
function addDays(iso, days) {
  const d = new Date(iso + 'T00:00:00')
  d.setDate(d.getDate() + days)
  return d.toISOString().slice(0, 10)
}

/**
 * The window the screen should open on.
 *
 * Matt, after the third refresh in an afternoon: "Can you make it so the post to core dates start
 * at the next run, rather than all the way back to July? It's getting old having to update these
 * date ranges each time I refresh the page." The screen used to open on the first day of the
 * import, every time.
 *
 * Three sources, in order:
 *   1. The window last used in this browser for this import, if it still has unposted payments —
 *      a refresh mid-window lands back on the same window.
 *   2. If that window is finished, the later of: the window after it, and the day after the
 *      latest payment this tool posted. "Later of" matters because a window left open only to
 *      LOOK at old months must not drag the default back there.
 *   3. With nothing remembered: the day after the latest posted payment, or the import's first
 *      day when nothing has been posted yet.
 *
 * Windows keep whatever alignment the person chose (Jan 1–14, Jan 15–28 ...), which is why the
 * remembered window is advanced by fourteen days rather than snapped to a grid from the import's
 * first day — that grid would have put Jan 1 in a window starting Dec 30.
 */
const WINDOW_KEY = (batchId) => 'aj-finance-post-window:' + batchId

function readWindow(batchId) {
  try {
    const w = JSON.parse(localStorage.getItem(WINDOW_KEY(batchId)) || 'null')
    return w && /^d{4}-d{2}-d{2}$/.test(w.from || '') && /^d{4}-d{2}-d{2}$/.test(w.to || '') ? w : null
  } catch { return null }
}
function saveWindow(batchId, from, to) {
  try { localStorage.setItem(WINDOW_KEY(batchId), JSON.stringify({ from, to })) } catch { /* ignore */ }
}

function defaultWindow(batch, postState) {
  const start = batch?.period_start || ''
  if (!start) return { from: '', to: '' }
  const open = (postState && postState.openDates) || []
  const hasOpen = (f, t) => open.some(d => d >= f && d <= t)
  const afterPosted = postState && postState.lastPostedOn ? addDays(postState.lastPostedOn, 1) : start
  const fresh = { from: afterPosted, to: addDays(afterPosted, 13) }

  const stored = readWindow(batch.id)
  if (!stored) return fresh
  if (hasOpen(stored.from, stored.to)) return stored
  const advanced = { from: addDays(stored.to, 1), to: addDays(stored.to, 14) }
  return advanced.from >= fresh.from ? advanced : fresh
}

const ACTION_STYLE = {
  post:    { label: 'will post',   color: '#2e7d32' },
  hold:    { label: 'held',        color: '#b4493c' },
  skip:    { label: 'skipped',     color: 'var(--aj-text-muted)' },
  posted:  { label: 'already in',  color: 'var(--aj-text-muted)' },
}



export default function PostAndReconcile({ S, batch, postState }) {
  const [initial]         = useState(() => defaultWindow(batch, postState))
  const [from, setFrom]   = useState(initial.from)
  const [to, setTo]       = useState(initial.to)
  const [plan, setPlan]   = useState(null)
  const [result, setResult] = useState(null)
  const [tie, setTie]     = useState(null)
  const [period, setPeriod] = useState(null)
  const [busy, setBusy]   = useState('')
  const [err, setErr]     = useState('')
  // The dry run is a picture of the rows as they were when it ran. This screen now stays
  // mounted while the person is on Review, and Review is where rows change: an account decided, a
  // vendor connected, a project chosen. When the import reloads after such a change, the picture
  // is stale and posting from it would mean posting from a table that no longer says what the
  // server will do. The server re-plans regardless, so nothing wrong can be written; but the
  // person would be pressing Post on a row that reads "held". So the table is marked and Post
  // waits for a fresh dry run.
  const [planState, setPlanState] = useState(null)   // the postState the current plan was run against
  const planStale = !!plan && planState !== postState
  useEffect(() => { if (!plan) setPlanState(null) }, [plan])

  // A new window invalidates everything shown for the old one. Leaving a stale plan on screen
  // next to a new date range is how the wrong batch gets posted.
  function setWindow(nextFrom, nextTo) {
    setFrom(nextFrom); setTo(nextTo)
    setPlan(null); setResult(null); setTie(null)
    if (batch?.id && nextFrom && nextTo) saveWindow(batch.id, nextFrom, nextTo)
  }

  async function run(key, fn, onOk) {
    setBusy(key); setErr('')
    try { onOk(await fn()) }
    catch (e) { setErr(e.message || String(e)) }
    finally { setBusy('') }
  }

  const doPlan    = () => {
    if (batch?.id && from && to) saveWindow(batch.id, from, to)   // a dry run means "this is my window"
    return run('plan', () => planCommit(batch.id, from, to), r => { setPlan(r); setPlanState(postState); setResult(null); setTie(null) })
  }
  const doTie     = () => run('tie', () => reconcileWindow(batch.id, from, to), setTie)
  const doPeriod  = () => run('period', () => reconcilePeriod(batch.id, batch.period_start, batch.period_end), setPeriod)

  async function doPost() {
    const willPost = (plan?.rows || []).filter(r => r.action === 'post')
    if (!willPost.length) return
    const ok = await confirmDialog({
      title: `Post ${willPost.length} payment${willPost.length === 1 ? '' : 's'} to Core?`,
      message: `${money(plan.totals.post)} between ${from} and ${to}. This creates real checks in `
        + 'BQE Core, which has no reversal. Everything here was recomputed on the server just now, '
        + 'and anything already in Core will be refused rather than written twice.',
      confirmLabel: `Post ${willPost.length}`,
    })
    if (!ok) return

    // Server caps a call at 40 rows, so a longer window goes in passes. Stop on the first
    // failure: a failed call is not an empty result, and continuing past one is how a run
    // reports "nothing to post" while payments wait.
    setBusy('post'); setErr('')
    // The server answers { attempted, created: <count>, refused: <count>, results: [{ id, ok,
    // refused? }] }. The first cut of this screen assumed `created` was a LIST and spread it,
    // which throws on a number — so the screen crashed AFTER the server had posted everything and
    // reported "0 posted" while 25 checks sat in Core. The ledger was right and the report was
    // wrong, which is the worse way round. Read the shape the server actually sends.
    const created = []
    try {
      const ids = willPost.map(r => r.id)
      for (let i = 0; i < ids.length; i += 40) {
        const res = await commitRows(ids.slice(i, i + 40))
        const results = Array.isArray(res.results) ? res.results : []
        created.push(...results.filter(x => x.ok))
        const refused = results.filter(x => !x.ok).map(x => ({ id: x.id, error: x.refused || 'refused', detail: x.detail || null }))
        if (refused.length) {
          setResult({ created, failed: refused })
          setErr(`Stopped after ${created.length}. ${refused.length} row(s) were refused — see below.`)
          setBusy('')
          return
        }
      }
      setResult({ created, failed: [] })
      // Tie out immediately. The whole point of a small window is finding a problem while it is
      // still this window's problem.
      const t = await reconcileWindow(batch.id, from, to)
      setTie(t)
      const fresh = await planCommit(batch.id, from, to)
      setPlan(fresh)
    } catch (e) {
      setErr(e.message || String(e))
      setResult({ created, failed: [] })
    } finally { setBusy('') }
  }

  if (!batch) return null

  const counts = plan?.counts || {}
  const willPost = (plan?.rows || []).filter(r => r.action === 'post')
  const held = (plan?.rows || []).filter(r => r.action === 'hold')

  // Why things were skipped, in the planner's own words, commonest first.
  const skipReasons = Object.entries(
    (plan?.rows || [])
      .filter(r => r.action === 'skip' || r.action === 'posted')
      .reduce((acc, r) => {
        const k = r.reason || 'No reason given.'
        acc[k] = (acc[k] || 0) + 1
        return acc
      }, {}),
  ).sort((a, b) => b[1] - a[1])

  return (
    <div style={S.card}>
      <div style={S.h}>Post to Core</div>

      <div style={{ fontSize: 13, color: 'var(--aj-text-mid)', marginBottom: 10 }}>
        Two weeks at a time. Dry run first, post, then tie out before moving on — a mistake found in
        one window is cheap, and Core has no undo.
      </div>

      {/* window */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
        <input type="date" value={from} onChange={e => setWindow(e.target.value, addDays(e.target.value, 13))} style={S.input} />
        <span style={{ color: 'var(--aj-text-muted)' }}>to</span>
        <input type="date" value={to} onChange={e => setWindow(from, e.target.value)} style={S.input} />
        <button style={S.btn} disabled={!!busy} onClick={() => setWindow(addDays(from, -14), addDays(from, -1))}>← previous</button>
        <button style={S.btn} disabled={!!busy} onClick={() => setWindow(addDays(to, 1), addDays(to, 14))}>next →</button>
        <button style={{ ...S.btn, ...S.primary }} disabled={!!busy || !from || !to} onClick={doPlan}>
          {busy === 'plan' ? 'Working…' : 'Dry run'}
        </button>
      </div>

      {err && <div style={{ fontSize: 13, color: '#b4493c', marginBottom: 10 }}>{err}</div>}

      {plan && (
        <>
          {/* 'skip' covers four different situations — already in Core, not a payment, money in,
              zero amount — and the first is by far the most common on a window that has been
              posted before. Calling the lot "not payments" told a reader that 22 real vendor
              payments had been ignored, when in fact every one of them was already recorded
              correctly. Never summarise a mixed bucket with the name of one of its members: say
              how many were skipped, then let them say why themselves. */}
          <div style={{ ...S.mono, fontSize: 13, marginBottom: 8 }}>
            {counts.post || 0} to post ({money(plan.totals.post)}) ·{' '}
            {counts.hold || 0} held ·{' '}
            {(counts.skip || 0) + (counts.posted || 0)} skipped
          </div>
          {skipReasons.length > 0 && (
            <div style={{ fontSize: 12, color: 'var(--aj-text-muted)', marginBottom: 8 }}>
              {skipReasons.map(([reason, n]) => (
                <div key={reason}>{n} × {reason}</div>
              ))}
            </div>
          )}

          {/* Vendors that do not exist in Core yet. Posting without them fails, and creating them
              carelessly is how a duplicate vendor record gets made. */}
          {plan.vendorsNeeded?.length > 0 && (
            <div style={{
              border: '0.5px solid #b4493c', borderRadius: 6, padding: 10, marginBottom: 10,
              fontSize: 13,
            }}>
              <strong>{plan.vendorsNeeded.length} payee(s) have no record in Core.</strong> Create
              them in Core first — and check the nearest existing name before you do, so the firm
              does not end up with two of the same vendor.
              <div style={{ marginTop: 6 }}>
                {plan.vendorsNeeded.slice(0, 12).map(v => (
                  <div key={v.vendor} style={{ ...S.mono, fontSize: 12 }}>
                    {v.vendor} <span style={{ color: 'var(--aj-text-muted)' }}>×{v.count}</span>
                    {v.nearest?.length ? <span style={{ color: '#b4493c' }}> — Core has: {v.nearest.map(n => (typeof n === 'string' ? n : n.name)).filter(Boolean).slice(0, 3).join(', ')}</span> : null}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* the plan */}
          <div style={{ maxHeight: 340, overflowY: 'auto', border: '0.5px solid var(--aj-border)', borderRadius: 6, marginBottom: 10 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                {['Date', 'Payee / description', 'Amount', 'What happens', 'Why'].map(h => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {plan.rows.map(r => {
                  const a = ACTION_STYLE[r.action] || ACTION_STYLE.skip
                  return (
                    <tr key={r.id} style={{ borderTop: '0.5px solid var(--aj-border)' }}>
                      <td style={{ ...S.td, ...S.mono, whiteSpace: 'nowrap' }}>{r.posted_on}</td>
                      <td style={S.td}>
                        <div style={{ fontSize: 13 }}>{r.payee || r.vendor || '—'}</div>
                        <div style={{ fontSize: 11, color: 'var(--aj-text-muted)' }}>{r.description}</div>
                        {r.project && (
                          <div style={{ fontSize: 11, color: '#2e7d32' }}>
                            {r.project} · {r.item}{r.billable === false ? ' · not billable' : ''}
                          </div>
                        )}
                      </td>
                      <td style={{ ...S.td, ...S.mono, textAlign: 'right', whiteSpace: 'nowrap' }}>{money(r.amount)}</td>
                      <td style={{ ...S.td, color: a.color, fontSize: 12, whiteSpace: 'nowrap' }}>
                        {a.label}
                        {r.legs?.length > 1 && (
                          <div style={{ ...S.mono, fontSize: 10, color: 'var(--aj-text-muted)' }}>
                            {r.legs.map(l => `${l.code} ${money(l.amount)}`).join(' + ')}
                          </div>
                        )}
                        {r.legs?.length === 1 && (
                          <div style={{ ...S.mono, fontSize: 10, color: 'var(--aj-text-muted)' }}>{r.legs[0].code}</div>
                        )}
                      </td>
                      <td style={{ ...S.td, fontSize: 11, color: r.action === 'hold' ? '#b4493c' : 'var(--aj-text-muted)' }}>
                        {r.reason || r.basis || ''}
                        {r.action === 'hold' && /No vendor or employee/.test(r.reason || '') && (
                          <div style={{ marginTop: 3, color: 'var(--aj-text-muted)' }}>
                            Decide this under <strong>2. Review</strong>, where the vendor decisions are.
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              style={{ ...S.btn, ...(willPost.length && !planStale ? S.primary : {}) }}
              disabled={!!busy || !willPost.length || planStale}
              onClick={doPost}
            >
              {busy === 'post' ? 'Posting…' : `Post ${willPost.length} to Core`}
            </button>
            {planStale && (
              <span style={{ fontSize: 12, color: '#b4493c' }}>
                Rows changed under Review since this dry run. Click Dry run again before posting.
              </span>
            )}
            <button style={S.btn} disabled={!!busy} onClick={doTie}>
              {busy === 'tie' ? 'Checking…' : 'Tie out this window'}
            </button>
            {held.length > 0 && (
              <span style={{ fontSize: 12, color: 'var(--aj-text-muted)' }}>
                {held.length} held — each one needs a decision above before it can post.
              </span>
            )}
          </div>
        </>
      )}

      {result && (
        <div style={{ marginTop: 12, fontSize: 13 }}>
          <strong>{result.created.length} posted.</strong>
          {result.failed?.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ color: '#b4493c', fontWeight: 600 }}>
                {result.failed.length} refused by Core. Nothing was written for {result.failed.length === 1 ? 'it' : 'them'}; everything counted above did post.
              </div>
              {result.failed.slice(0, 8).map((f, i) => {
                // Name the row the way the table does. A person recognises "$224.25 to CNB Building
                // on 2026-04-01"; nobody recognises the first eight characters of a uuid.
                const row = (plan?.rows || []).find(r => r.id === f.id)
                return (
                  <div key={i} style={{ marginTop: 8, padding: '8px 10px', border: '0.5px solid #b4493c', borderRadius: 6, background: 'var(--aj-bg-subtle)' }}>
                    {row && (
                      <div style={{ fontSize: 13 }}>
                        <span style={S.mono}>{row.posted_on}</span>
                        {' · '}<strong>{row.payee || row.vendor}</strong>
                        {' · '}<span style={S.mono}>{money(row.amount)}</span>
                        {row.project && <span style={{ color: 'var(--aj-text-muted)' }}>{' · '}{row.project}{row.item ? ' · ' + row.item : ''}</span>}
                      </div>
                    )}
                    <div style={{ fontSize: 13, marginTop: 4 }}>{f.error}</div>
                    {f.detail && (
                      <details style={{ marginTop: 4 }}>
                        <summary style={{ fontSize: 11, color: 'var(--aj-text-muted)', cursor: 'pointer' }}>What Core actually said</summary>
                        <div style={{ ...S.mono, fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 4, wordBreak: 'break-all' }}>{f.detail}</div>
                      </details>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* The server answers { bank:{rows,outflow}, entered:{rows,total}, outstanding:{rows,total},
          heldDeliberately:{rows,total}, amountMismatches:[…], unexplainedCoreChecks:[…],
          complete, verdict }. Read THAT — the first cut read fields that do not exist and would
          have printed "Window ties out · entered $0.00" over any answer at all. */}
      {tie && (() => {
        const mism = tie.amountMismatches || []
        const bad = mism.length > 0
        return (
          <div style={{
            marginTop: 12, padding: 10, borderRadius: 6, fontSize: 13,
            border: '0.5px solid ' + (bad ? '#b4493c' : tie.complete ? '#2e7d32' : 'var(--aj-border-mid)'),
          }}>
            {bad
              ? <>
                  <strong style={{ color: '#b4493c' }}>{mism.length} check(s) do not equal what they cover.</strong>
                  <div style={{ fontSize: 12, marginTop: 4 }}>
                    A check worth more than its rows means money counted twice. Fix this before posting anything else.
                  </div>
                  {mism.slice(0, 8).map((m, i) => (
                    <div key={i} style={{ ...S.mono, fontSize: 11, marginTop: 3 }}>{JSON.stringify(m)}</div>
                  ))}
                </>
              : <>
                  <strong style={{ color: tie.complete ? '#2e7d32' : 'var(--aj-text)' }}>
                    {tie.complete ? 'Window ties out.' : (tie.verdict || 'Window checked.')}
                  </strong>{' '}
                  <span style={{ ...S.mono, fontSize: 12, color: 'var(--aj-text-mid)' }}>
                    bank {tie.bank?.rows ?? '?'} payments {money(tie.bank?.outflow)} ·
                    entered {tie.entered?.rows ?? 0} {money(tie.entered?.total)} ·
                    not yet {tie.outstanding?.rows ?? 0} {money(tie.outstanding?.total)}
                    {tie.heldDeliberately?.rows ? ` · set aside ${tie.heldDeliberately.rows}` : ''}
                  </span>
                  <div style={{ fontSize: 12, color: 'var(--aj-text-muted)', marginTop: 4 }}>
                    Every entered check equals the bank lines it covers — nothing counted twice.
                    {tie.unexplainedCoreChecks?.length ? ` ${tie.unexplainedCoreChecks.length} Core check(s) in this window have no bank line.` : ''}
                  </div>
                </>}
          </div>
        )
      })()}

      {/* the whole-period check, separate on purpose: it answers a different question */}
      <div style={{ marginTop: 14, paddingTop: 12, borderTop: '0.5px solid var(--aj-border)' }}>
        <button style={S.btn} disabled={!!busy} onClick={doPeriod}>
          {busy === 'period' ? 'Checking…' : 'Does the whole import tie to the bank?'}
        </button>
        {period && (
          <div style={{ marginTop: 8, fontSize: 13 }}>
            <div style={{ ...S.mono }}>
              bank net {money(period.bank?.net)} · Core net {money(period.core?.net)}
              {period.outDifference != null && ` · money out differs by ${money(period.outDifference)}`}
              {period.inDifference != null && ` · money in differs by ${money(period.inDifference)}`}
            </div>
            {period.core?.directPayments && (
              <div style={{ fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 4 }}>
                Core money in = {period.core.deposits?.n} deposits ({money(period.core.deposits?.total)}) +{' '}
                {period.core.directPayments.n} payments booked straight to this account ({money(period.core.directPayments.total)})
                {period.core.transfersIn ? <> + {period.core.transfersIn.n} transfer(s) in from the other Chase account ({money(period.core.transfersIn.total)})</> : null}.
                {period.core.transfersOut ? <> Core money out = {period.core.checks?.n} checks ({money(period.core.checks?.total)}) + {period.core.transfersOut.n} transfer(s) out ({money(period.core.transfersOut.total)}).</> : null}
                {period.core.directPayments.unreadable > 0 && (
                  <span style={{ color: '#b4493c' }}> Core would not return {period.core.directPayments.unreadable} payment ledger(s), so money in is understated by those; run this again.</span>
                )}
              </div>
            )}
            {period.difference != null && (
              <div style={{ color: Math.abs(period.difference) < 0.005 ? '#2e7d32' : '#b4493c', marginTop: 4 }}>
                {Math.abs(period.difference) < 0.005
                  ? 'The books move by the same amount the bank does.'
                  : `Out by ${money(period.difference)} — the books and the bank do not agree yet.`}
              </div>
            )}
            {period.note && <div style={{ fontSize: 12, color: 'var(--aj-text-muted)', marginTop: 4 }}>{period.note}</div>}
          </div>
        )}
      </div>
    </div>
  )
}
